import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Ticket, Plus, Loader2, Copy, Trash2, CheckCircle, Clock, XCircle, User, PlusCircle,
} from 'lucide-react';

const CreditBundlesTab = () => {
  const [bundles, setBundles] = useState([]);
  const [allowedClasses, setAllowedClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const emptyForm = {
    student_name: '',
    parent_email: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_phone: '',
    class_ids: [],
    notes: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [justCreated, setJustCreated] = useState(null); // {pay_url, ...}

  // Top-up state
  const [topupBundle, setTopupBundle] = useState(null);
  const [topupCredits, setTopupCredits] = useState(6);
  const [topupAmount, setTopupAmount] = useState(60);
  const [topupNote, setTopupNote] = useState('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [topupLink, setTopupLink] = useState(null);
  const [topupLinkLoading, setTopupLinkLoading] = useState(false);
  const [chargingCard, setChargingCard] = useState(false);

  const handleChargeSavedCard = async () => {
    if (!topupBundle) return;
    if (topupCredits <= 0) {
      toast.error('Extra credits must be greater than 0');
      return;
    }
    const amount = ((Number(topupCredits) || 0) * (topupBundle.price_per_credit || 10)).toFixed(2);
    const confirmed = window.confirm(
      `Charge £${amount} to ${topupBundle.student_name}'s saved card ending ${topupBundle.card_last4 || '????'}?\n\n${topupCredits} credits will be added immediately.`
    );
    if (!confirmed) return;
    setChargingCard(true);
    try {
      const res = await api.post(`/admin/credit-bundles/${topupBundle.id}/charge-topup`, {
        extra_credits: Number(topupCredits),
      });
      toast.success(`Charged £${res.data.amount_charged?.toFixed(2)} — ${res.data.extra_credits} credits added`);
      setTopupBundle(null);
      setTopupLink(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Charge failed');
    } finally {
      setChargingCard(false);
    }
  };

  const handleGenerateTopupLink = async () => {
    if (!topupBundle) return;
    if (topupCredits <= 0) {
      toast.error('Extra credits must be greater than 0');
      return;
    }
    setTopupLinkLoading(true);
    try {
      const res = await api.post(`/admin/credit-bundles/${topupBundle.id}/topup-link`, {
        extra_credits: Number(topupCredits),
      });
      setTopupLink(res.data);
      toast.success(res.data.reused ? 'Existing pay link reused' : 'Pay link generated');
    } catch (err) {
      toast.error('Failed to generate link: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTopupLinkLoading(false);
    }
  };

  const handleTopup = async () => {
    if (!topupBundle) return;
    if (topupCredits <= 0) {
      toast.error('Extra credits must be greater than 0');
      return;
    }
    setTopupSubmitting(true);
    try {
      await api.post(`/admin/credit-bundles/${topupBundle.id}/topup`, {
        extra_credits: Number(topupCredits),
        extra_amount_paid: Number(topupAmount) || 0,
        note: topupNote,
      });
      toast.success(`Added ${topupCredits} credits to ${topupBundle.student_name}`);
      setTopupBundle(null);
      setTopupLink(null);
      setTopupCredits(6);
      setTopupAmount(60);
      setTopupNote('');
      fetchAll();
    } catch (err) {
      toast.error('Top-up failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTopupSubmitting(false);
    }
  };

  // Mark as paid state
  const [markPaidBundle, setMarkPaidBundle] = useState(null);
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markPaidForce, setMarkPaidForce] = useState(false);
  const [markPaidPiOverride, setMarkPaidPiOverride] = useState('');
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);

  const handleMarkPaid = async () => {
    if (!markPaidBundle) return;
    if (markPaidForce && !markPaidNote.trim()) {
      toast.error('A note is required when force-marking without Stripe verification.');
      return;
    }
    setMarkPaidSubmitting(true);
    try {
      const res = await api.post(`/admin/credit-bundles/${markPaidBundle.id}/mark-paid`, {
        force: markPaidForce,
        note: markPaidNote,
        payment_intent_id: markPaidPiOverride.trim() || undefined,
      });
      if (res.data?.already) {
        toast.info('This bundle was already marked paid.');
      } else {
        toast.success(`${markPaidBundle.student_name}'s bundle is now paid.`);
      }
      setMarkPaidBundle(null);
      setMarkPaidNote('');
      setMarkPaidForce(false);
      setMarkPaidPiOverride('');
      fetchAll();
    } catch (err) {
      toast.error('Mark paid failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bundlesRes, classesRes] = await Promise.all([
        api.get('/admin/credit-bundles'),
        api.get('/admin/credit-bundles/allowed-classes'),
      ]);
      setBundles(bundlesRes.data || []);
      setAllowedClasses(classesRes.data || []);
    } catch (err) {
      toast.error('Failed to load credit bundles: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleClass = (id) => {
    setForm(f => ({
      ...f,
      class_ids: f.class_ids.includes(id)
        ? f.class_ids.filter(x => x !== id)
        : [...f.class_ids, id],
    }));
  };

  const handleCreate = async () => {
    if (!form.student_name.trim() || !form.parent_email.trim()) {
      toast.error('Student name and parent email are required');
      return;
    }
    if (form.class_ids.length === 0) {
      toast.error('Select at least one class');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/admin/credit-bundles', form);
      setJustCreated(res.data);
      setForm(emptyForm);
      fetchAll();
      toast.success(`Bundle created for ${res.data.student_name}`);
    } catch (err) {
      toast.error('Create failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (bundle) => {
    if (!window.confirm(`Delete bundle for ${bundle.student_name}? (Only unpaid or unused bundles can be deleted.)`)) return;
    try {
      await api.delete(`/admin/credit-bundles/${bundle.id}`);
      toast.success('Bundle deleted');
      fetchAll();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Pay link copied to clipboard');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const statusBadge = (b) => {
    const remaining = (b.credits_total || 0) - (b.credits_used || 0);
    if (b.status === 'pending') {
      return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30"><Clock className="w-3 h-3 mr-1" />Awaiting payment</Badge>;
    }
    if (b.status === 'used_up') {
      return <Badge className="bg-slate-500/20 text-slate-300 border-slate-500/30"><XCircle className="w-3 h-3 mr-1" />Used up</Badge>;
    }
    if (b.status === 'paid') {
      return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" />{remaining}/{b.credits_total} credits left</Badge>;
    }
    return <Badge>{b.status}</Badge>;
  };

  const frontendUrl = window.location.origin;

  return (
    <div className="space-y-4">
      <Card className="bg-[#111] border-[#333]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#FF007F]" />
              PAYG Credit Bundles
            </CardTitle>
            <Button
              onClick={() => { setJustCreated(null); setCreateModalOpen(true); }}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
              data-testid="create-bundle-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Bundle
            </Button>
          </div>
          <p className="text-xs text-white/50 mt-1">
            6 credits for £60 (£10 per class). Eligible classes: Adult Street Fitness & Adult Street Choreography.
            Not advertised publicly — share the pay link only with the parent.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-white/50 py-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
          ) : bundles.length === 0 ? (
            <p className="text-white/50 text-sm py-8 text-center">No credit bundles yet. Click "New Bundle" to create one.</p>
          ) : (
            <div className="space-y-2">
              {bundles.map(b => {
                const remaining = (b.credits_total || 0) - (b.credits_used || 0);
                const payUrl = `${frontendUrl}/credits/${b.token}`;
                return (
                  <div key={b.id} className="border border-[#222] rounded-lg p-4 bg-[#0a0a0a]">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <User className="w-4 h-4 text-white/50" />
                          <h4 className="text-white font-semibold">{b.student_name}</h4>
                          {statusBadge(b)}
                        </div>
                        <p className="text-xs text-white/50 mt-1">
                          {b.parent_email}{b.parent_phone ? ` • ${b.parent_phone}` : ''}
                        </p>
                        <p className="text-xs text-white/50 mt-1">
                          Classes: {(b.allowed_class_names || []).join(', ') || '—'}
                        </p>
                        {b.status === 'paid' && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="h-1.5 flex-1 bg-[#1a1a1a] rounded overflow-hidden max-w-xs">
                              <div
                                className="h-full bg-[#FF007F]"
                                style={{ width: `${(remaining / b.credits_total) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-white/60">{remaining}/{b.credits_total} left</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {b.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyLink(payUrl)}
                              className="border-[#333] text-white hover:bg-white/5"
                              data-testid={`copy-pay-link-${b.id}`}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy pay link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setMarkPaidBundle(b);
                                setMarkPaidNote('');
                                setMarkPaidForce(false);
                                setMarkPaidPiOverride('');
                              }}
                              className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                              data-testid={`mark-paid-bundle-${b.id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Mark paid
                            </Button>
                          </>
                        )}
                        {(b.status === 'paid' || b.status === 'used_up') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTopupBundle(b);
                              setTopupLink(null);
                              setTopupCredits(6);
                              setTopupAmount(60);
                              setTopupNote('');
                            }}
                            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`topup-bundle-${b.id}`}
                          >
                            <PlusCircle className="w-3 h-3 mr-1" />
                            Top up
                          </Button>
                        )}
                        {(b.status === 'pending' || (b.credits_used || 0) === 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(b)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#FF007F]" />
              {justCreated ? 'Bundle Created' : 'New PAYG Credit Bundle'}
            </DialogTitle>
          </DialogHeader>

          {justCreated ? (
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-emerald-300 font-medium">Bundle created for {justCreated.student_name}</p>
                <p className="text-white/60 text-sm mt-1">
                  {justCreated.credits_total} credits for £{justCreated.amount_total?.toFixed(2)}
                </p>
              </div>
              <div>
                <Label className="text-white/70 text-sm">Pay link (share via email or WhatsApp)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={justCreated.pay_url}
                    readOnly
                    className="bg-[#0a0a0a] border-[#333] text-white text-xs font-mono"
                    data-testid="created-bundle-link"
                  />
                  <Button
                    onClick={() => copyLink(justCreated.pay_url)}
                    className="bg-[#FF007F] hover:bg-[#D6006A]"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => { setCreateModalOpen(false); setJustCreated(null); }}
                  variant="outline"
                  className="border-[#333] text-white"
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-white/70">Student name *</Label>
                <Input
                  value={form.student_name}
                  onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                  placeholder="e.g. Jane Doe"
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  data-testid="bundle-student-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/70">Parent first name</Label>
                  <Input
                    value={form.parent_first_name}
                    onChange={(e) => setForm({ ...form, parent_first_name: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Parent last name</Label>
                  <Input
                    value={form.parent_last_name}
                    onChange={(e) => setForm({ ...form, parent_last_name: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Parent email *</Label>
                <Input
                  type="email"
                  value={form.parent_email}
                  onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
                  placeholder="parent@example.com"
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  data-testid="bundle-parent-email"
                />
              </div>
              <div>
                <Label className="text-white/70">Parent phone</Label>
                <Input
                  value={form.parent_phone}
                  onChange={(e) => setForm({ ...form, parent_phone: e.target.value })}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Eligible classes *</Label>
                <p className="text-xs text-white/40 mt-1 mb-2">Tick the classes these credits can be used for.</p>
                <div className="space-y-1">
                  {allowedClasses.length === 0 ? (
                    <p className="text-white/40 text-xs">No eligible classes found in the academy. Check Adult Street Fitness / Choreography exist.</p>
                  ) : allowedClasses.map(c => (
                    <label
                      key={c.id}
                      className="flex items-start gap-3 p-2 bg-[#0a0a0a] border border-[#222] rounded cursor-pointer hover:border-[#FF007F]/50"
                      data-testid={`bundle-class-toggle-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.class_ids.includes(c.id)}
                        onChange={() => toggleClass(c.id)}
                        className="mt-1 accent-[#FF007F] w-4 h-4"
                      />
                      <div>
                        <p className="text-white text-sm">{c.name}</p>
                        {(c.day_of_week || c.start_time) && (
                          <p className="text-xs text-white/50">
                            {c.day_of_week}
                            {c.start_time && ` ${c.start_time}`}
                            {c.end_time && ` - ${c.end_time}`}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-white/70">Internal notes (optional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                />
              </div>
              <div className="bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg p-3 text-sm">
                <p className="text-white">
                  <span className="text-white/50">Total:</span> <span className="font-bold">£60.00</span> for 6 credits (£10 per class)
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCreateModalOpen(false)}
                  className="border-[#333] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-[#FF007F] hover:bg-[#D6006A]"
                  data-testid="bundle-submit-btn"
                >
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Create & Get Pay Link'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Top Up Bundle Dialog */}
      <Dialog open={!!topupBundle} onOpenChange={(o) => { if (!o) { setTopupBundle(null); setTopupLink(null); } }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-400" />
              Top Up Credits
            </DialogTitle>
          </DialogHeader>
          {topupBundle && (
            <div className="space-y-3">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-sm">
                <p className="text-white"><span className="text-white/50">Student:</span> {topupBundle.student_name}</p>
                <p className="text-white/70 mt-1"><span className="text-white/50">Currently:</span> {(topupBundle.credits_total || 0) - (topupBundle.credits_used || 0)}/{topupBundle.credits_total} credits left, paid £{(topupBundle.amount_paid || 0).toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-white/70">Extra credits to add *</Label>
                <Input
                  type="number"
                  min="1"
                  value={topupCredits}
                  onChange={(e) => setTopupCredits(e.target.value)}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  data-testid="topup-credits-input"
                />
              </div>
              <div>
                <Label className="text-white/70">Extra payment received (£)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                  data-testid="topup-amount-input"
                />
                <p className="text-xs text-white/40 mt-1">Set to 0 if it's a gift / free top-up.</p>
              </div>
              <div>
                <Label className="text-white/70">Reason (optional)</Label>
                <Input
                  value={topupNote}
                  onChange={(e) => setTopupNote(e.target.value)}
                  placeholder="e.g. duplicate Stripe charge — honoured"
                  className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                />
              </div>

              <div className="border-t border-[#222] pt-3">
                {topupBundle.stripe_payment_method_id && (
                  <div className="mb-3">
                    <Button
                      size="sm"
                      onClick={handleChargeSavedCard}
                      disabled={chargingCard}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                      data-testid="topup-charge-saved-card"
                    >
                      {chargingCard ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Charging…</>
                      ) : (
                        <>Charge saved card {topupBundle.card_brand ? topupBundle.card_brand.toUpperCase() : ''} •••• {topupBundle.card_last4 || '????'} — £{((Number(topupCredits) || 0) * (topupBundle.price_per_credit || 10)).toFixed(2)}</>
                      )}
                    </Button>
                    <p className="text-[11px] text-white/40 mt-1 text-center">Charges instantly and adds {topupCredits || 0} credits — no link needed</p>
                  </div>
                )}
                <p className="text-xs text-white/50 mb-2">
                  <strong className="text-white/70">Haven't taken payment yet?</strong> Generate a Stripe pay link to send to the parent —
                  credits are only added once they pay (£{((Number(topupCredits) || 0) * (topupBundle.price_per_credit || 10)).toFixed(2)} for {topupCredits || 0} credits).
                </p>
                {topupLink ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 space-y-2" data-testid="topup-link-box">
                    <p className="text-xs text-emerald-300 break-all font-mono">{topupLink.pay_url}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/40 text-emerald-300 h-7 text-xs"
                      onClick={() => copyLink(topupLink.pay_url)}
                      data-testid="topup-link-copy"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy link
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGenerateTopupLink}
                    disabled={topupLinkLoading}
                    className="border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10"
                    data-testid="topup-generate-link"
                  >
                    {topupLinkLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…</> : 'Generate pay link'}
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTopupBundle(null)} className="border-[#333] text-white">Cancel</Button>
            <Button onClick={handleTopup} disabled={topupSubmitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-submit">
              {topupSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</> : 'Add credits'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={!!markPaidBundle} onOpenChange={(o) => { if (!o) setMarkPaidBundle(null); }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Mark bundle as paid
            </DialogTitle>
          </DialogHeader>
          {markPaidBundle && (
            <div className="space-y-3">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-white/50">Student:</span> <span className="text-white">{markPaidBundle.student_name}</span></p>
                <p><span className="text-white/50">Parent:</span> <span className="text-white">{markPaidBundle.parent_email}</span></p>
                <p><span className="text-white/50">Amount:</span> <span className="text-white">£{(markPaidBundle.amount_total || 0).toFixed(2)}</span></p>
                <p><span className="text-white/50">Stripe PI:</span> <span className="text-white font-mono text-xs">{markPaidBundle.stripe_payment_intent_id || '— none —'}</span></p>
              </div>

              {markPaidBundle.stripe_payment_intent_id && !markPaidForce && (
                <p className="text-xs text-emerald-300/80">
                  ✓ Will verify the Stripe PaymentIntent is <strong>succeeded</strong> before flipping to paid.
                </p>
              )}

              {/* Override PI — used when the original PI is dead (e.g. requires_payment_method)
                  but a different PI succeeded (typical guest-checkout retry). */}
              {!markPaidForce && (
                <div>
                  <Label className="text-white/70">
                    Different succeeded PaymentIntent ID? <span className="text-white/40">(optional)</span>
                  </Label>
                  <Input
                    value={markPaidPiOverride}
                    onChange={(e) => setMarkPaidPiOverride(e.target.value)}
                    placeholder="pi_3T... (paste from Stripe dashboard)"
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1 font-mono text-xs"
                    data-testid="mark-paid-pi-override"
                  />
                  <p className="text-[11px] text-white/40 mt-1">
                    Paste a different PI ID if the original checkout was abandoned and the parent paid via guest checkout under a new PI.
                  </p>
                </div>
              )}

              <label className="flex items-start gap-2 p-2 rounded border border-amber-500/30 bg-amber-500/5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markPaidForce}
                  onChange={(e) => setMarkPaidForce(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500"
                  data-testid="mark-paid-force"
                />
                <span className="text-xs text-amber-200">
                  <strong>Force</strong> — skip Stripe check (use if paid outside the app: bank transfer, cash, different PI). Note required.
                </span>
              </label>

              {markPaidForce && (
                <div>
                  <Label className="text-white/70">Note *</Label>
                  <Input
                    value={markPaidNote}
                    onChange={(e) => setMarkPaidNote(e.target.value)}
                    placeholder="e.g. paid via bank transfer 2026-05-05"
                    className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                    data-testid="mark-paid-note"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMarkPaidBundle(null)} className="border-[#333] text-white">Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={markPaidSubmitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-paid-submit">
              {markPaidSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Mark as paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditBundlesTab;
